import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import {
  MaintenanceWindowRecord,
  MaintenanceWindowSelectorWebComponentSchema,
  MaintenanceWindowSelectorWebComponentViewData,
} from '../types';
import { htmlEncode } from '../../../utils/escape.utils';
import { MaintenanceWindowTypes } from '../../../types/common/maintenanceWindowTypes';
import { WEB_COMPONENT_MAINTENANCE_WINDOW_PREVIEW_PATCH_MANAGEMENT } from '../../../webcomponents.constants';
import { SelectOption } from '../../../ui/gzDropdown/types';

export class MaintenanceWindowSelectorView extends BaseWebComponentView {
  protected previewComponents = {
    [MaintenanceWindowTypes.PATCH_MANAGEMENT]: WEB_COMPONENT_MAINTENANCE_WINDOW_PREVIEW_PATCH_MANAGEMENT,
  };

  protected currentPreviewType: number | null = null;

  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');

    // Texts
    this.dom.$title = this.shadow.querySelector('#title');
    this.dom.$description = this.shadow.querySelector('#description');
    this.dom.$selectorLabel = this.shadow.querySelector('#selector-label');

    // Controls
    this.dom.$selector = this.shadow.querySelector('#selector');
    // TODO: for CERES-26375 uncomment dom query and remove hidden attribute from button in template
    // this.dom.$editButton = this.shadow.querySelector('#edit-button');

    // Containers
    this.dom.$previewWrapper = this.shadow.querySelector('#preview-wrapper');
    this.dom.$previewContainer = this.shadow.querySelector('#preview-container');

    // Setup translations
    this.getTranslations();
    this.setTranslatedStrings();
  }

  setTranslatedStrings() {
    this.dom.$title.innerHTML = htmlEncode(this.dependencies.translateFn('Patch Management'));
    this.dom.$description.innerHTML = htmlEncode(
      this.dependencies.translateFn('Select the maintenance window that you would like to assign to this policy.')
    );
    this.dom.$selectorLabel.innerHTML = htmlEncode(this.dependencies.translateFn('Maintenance windows')); // TODO: add : after label
    // TODO: for CERES-26375
    // this.dom.$editButton.innerHTML = htmlEncode(this.dependencies.translateFn('Edit window'));

    this.dom.$selector.errorMessage = htmlEncode(
      this.dependencies.translateFn('Could not load maintenance window list')
    );
  }

  addPreviewComponent(type: number | null) {
    if (type === null) {
      throw new Error('Maintenance window type not set');
    }

    if (!this.previewComponents[type]) {
      throw new Error(`Unknown maintenance window type: ${type}`);
    }

    if (type === this.currentPreviewType) {
      return;
    }

    this.dom.$preview = document.createElement(this.previewComponents[type]);
    this.dom.$previewContainer.appendChild(this.dom.$preview);

    this.dom.$preview.injectDependencies(this.dependencies);

    this.currentPreviewType = type;
  }

  buildSuccessView(data: MaintenanceWindowSelectorWebComponentViewData) {
    this.dom.$selector.options = this.convertToTempateOptions(data.maintenanceWindows, data.readOnly);
    this.dom.$selector.setFormValue(data.selected);
    this.dom.$preview.start();
  }

  buildErrorView(error: Error) {
    // Set error
    this.setErrorState();
  }

  clearTemplateValues() {
    this.dom.$selector.options = this.convertToTempateOptions();
    this.dom.$selector.value = '';
    this.dom.$selector.setSelectedOption();

    this.dom.$previewWrapper.setAttribute('hidden', 'hidden');
  }

  convertToTempateOptions(
    maintenanceWindows: Array<MaintenanceWindowRecord> = [],
    readOnly: boolean = false
  ): Array<SelectOption> {
    const options = [] as Array<SelectOption>;

    if (!readOnly || maintenanceWindows.length === 0) {
      options.push(this.getNoSelectionOption());
    }

    maintenanceWindows.forEach((record: MaintenanceWindowRecord) => {
      options.push({
        value: htmlEncode(record._id),
        title: htmlEncode(record.name),
      });
    });

    return options;
  }

  getNoSelectionOption(): SelectOption {
    return {
      value: '',
      title: htmlEncode(this.dependencies.translateFn('No maintenance window selected')),
    };
  }

  setLoadingState() {
    this.dom.$selector.isLoading = true;
    this.dom.$selector.isError = false;
  }

  setErrorState() {
    this.dom.$selector.isLoading = false;
    this.dom.$selector.isError = true;
  }

  removeAllStates() {
    this.dom.$selector.isLoading = false;
    this.dom.$selector.isError = false;
  }
}
